import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateSection } from "../../../Redux/Store/Slices/projectReducer";
import { API } from "../../../Services/axios";
import { URLS } from "../../../Services/Urls";
import { toast } from "react-toastify";

const InputField = ({ input, sectionId, onValueChange }) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(input.value || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValue(input.value || "");
  }, [input.value]);

  const handleChange = async (newValue) => {
    setValue(newValue);

    if (onValueChange) {
      onValueChange(input.id, newValue);
    }

      // Save to backend if input has an ID (persisted)
    try {
      if (input.id) {
        setIsSaving(true);
        await API.patch(URLS.PROJECT.updateInput(input.id), {
          value: newValue,
        });
      }

      // Update Redux state
      const updatedInput = {
        ...input,
        value: newValue,
      };

      // Note: We would need to update the section in Redux to reflect the input change
      // This is handled by the parent component
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'input:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setIsSaving(false);
    }
  };

  const renderInput = () => {
    // Normalize input_type to uppercase for consistent comparison
    const inputType = (input.input_type || "TEXT").toUpperCase();
    const commonProps = {
      id: `input-${input.id}`,
      value: value,
      onChange: (e) => handleChange(e.target.value),
      disabled: isSaving,
      className: "form-control",
      placeholder: input.placeholder || input.input_label || "",
    };

    switch (inputType) {
      case "NUMBER":
        return (
          <div>
            <input
              {...commonProps}
              type="number"
              min={input.min_value || undefined}
              max={input.max_value || undefined}
              step={input.step || 1}
              required={input.input_required || false}
            />
            {input.input_unity && (
              <span className="ms-2 text-muted">({input.input_unity})</span>
            )}
          </div>
        );

      case "SELECT":
        return (
          <select {...commonProps}>
            <option value="">{input.placeholder || "Sélectionner..."}</option>
            {input.options?.map((option, idx) => (
              <option key={idx} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </select>
        );

      case "TEXTAREA":
        return (
          <textarea
            {...commonProps}
            rows={input.rows || 3}
            style={{ resize: "vertical" }}
          />
        );

      case "CHECKBOX":
        return (
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={value === true || value === "true"}
              onChange={(e) => handleChange(e.target.checked)}
              disabled={isSaving}
              id={`input-${input.id}`}
            />
            <label className="form-check-label" htmlFor={`input-${input.id}`}>
              {input.label}
            </label>
          </div>
        );

      case "RADIO":
        return (
          <div>
            {input.options?.map((option, idx) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              return (
                <div key={idx} className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`input-${input.id}`}
                    id={`input-${input.id}-${idx}`}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={(e) => handleChange(e.target.value)}
                    disabled={isSaving}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`input-${input.id}-${idx}`}
                  >
                    {optionLabel}
                  </label>
                </div>
              );
            })}
          </div>
        );

      default:
        return <input {...commonProps} type="text" />;
    }
  };

  return (
    <div className="mb-3">
      <label htmlFor={`input-${input.id}`} className="form-label">
        {input.input_label || input.label || "Input"}
        {(input.input_required || input.required) && <span className="text-danger"> *</span>}
      </label>
      {input.input_info && (
        <small className="form-text text-muted d-block mb-2">
          {input.input_info}
        </small>
      )}
      {input.description && !input.input_info && (
        <small className="form-text text-muted d-block mb-2">
          {input.description}
        </small>
      )}
      {renderInput()}
      {input.suffix && (
        <small className="form-text text-muted ms-2">{input.suffix}</small>
      )}
      {isSaving && (
        <small className="form-text text-muted ms-2">
          <i className="fas fa-spinner fa-spin"></i> Enregistrement...
        </small>
      )}
    </div>
  );
};

export default InputField;

